import { Instant, LocalDate, LocalDateTime, ZoneId } from "@js-joda/core";
import assertNever from "assert-never";
import React from "react";
import useSWR from "swr";
import { API } from "../../API";
import useMixin from "../../hooks/useMixin";
import { isLoading } from "../../Loading";
import {
  PatientContractDiagnosisCodeId,
  PatientContractDiagnosisSetParams,
  PatientDocumentTypeId,
  PatientId,
  PatientMedicationProfileCreateParams,
  PatientMedicationProfileWithDetails,
  PatientTaskInstanceId,
} from "../../messages";
import {
  DocumentReplaceConstants,
  PatientDocumentAnswers,
  PatientDocumentScanCreate,
  PatientDocumentTaskInstanceSubmitRequest,
} from "../../messages/PatientDocuments";
import { VitalExtraDetails } from "../../messages/Vital";
import { STRINGS } from "../../strings";
import { AppState } from "../../system/AppState";
import { translate } from "../../translate";
import { fmap } from "../../utils/fmap";
import LoadingScaffold from "../LoadingScaffold";
import NotFoundScaffold from "../NotFoundScaffold";
import { PatientTaskInstanceState } from "../PatientTaskInstanceTypes";
import { ScheduledVisitDocScreen, ScheduledVisitDocScreenProps } from "../ScheduledVisitDocScreen";
import { simpleAlert } from "../SimpleAlert";
import { PatientTaskInstanceDocumentState } from "./PatienttaskInstanceDocumentTypes";
import { getResubmissionReasonFromDocuments } from "../../utils/taskUtils";

interface Props {
  api: API;
  appState: AppState;
  patientId: PatientId;
  patientTaskInstanceId: PatientTaskInstanceId;
  patientDocumentTypeId: PatientDocumentTypeId;
  onClickAddPhysician: (patientId: PatientId) => void;
  onClickGoBack: () => void;
}

const PatientTaskInstanceDocumentActivity = (props: Props) => {
  const [documentState, setDocument] = React.useState<PatientTaskInstanceDocumentState>({
    type: "Loading",
  });
  const [patientTaskInstanceState, setPatientTaskInstanceState] =
    React.useState<PatientTaskInstanceState>({ type: "Loading" });

  const { appTasksState } = useMixin(props.appState);
  const documentFetcher = () =>
    props.api.getPatientTaskDocument(props.patientTaskInstanceId, props.patientDocumentTypeId);

  useSWR(
    `patient_task_instances/${props.patientTaskInstanceId}/documents/${props.patientDocumentTypeId}`,
    documentFetcher,
    {
      onSuccess: (res) =>
        setDocument({
          type: "Ready",
          document: res.document,
        }),
      onError: () => setDocument({ type: "NotFound" }),
    }
  );

  React.useEffect(() => {
    if (!isLoading(appTasksState)) {
      const patientTaskInstance = appTasksState.tasks.find(
        (x) => x.id === props.patientTaskInstanceId
      );

      if (patientTaskInstance === undefined) {
        setPatientTaskInstanceState({ type: "NotFound" });
        return;
      }

      setPatientTaskInstanceState({
        type: "Ready",
        patientTaskInstance: patientTaskInstance,
      });
    }
  }, [appTasksState, props.patientTaskInstanceId]);

  const handleCompleteDocument = React.useCallback(
    (pdfUrl: string | null) => {
      if (patientTaskInstanceState.type !== "Ready") {
        throw new Error("Task not ready");
      }

      const newPatientTaskInstanceState: PatientTaskInstanceState = { ...patientTaskInstanceState };
      newPatientTaskInstanceState.patientTaskInstance.taskDetails.documents =
        newPatientTaskInstanceState.patientTaskInstance.taskDetails.documents.map((doc) => {
          if (
            doc.type === "PatientTaskInstanceDocumentRegular" &&
            doc.id === props.patientDocumentTypeId
          ) {
            if (pdfUrl === null) {
              return {
                ...doc,
                completed: true,
              };
            } else {
              return {
                type: "PatientTaskInstanceDocumentRegularScanned",
                id: doc.id,
                name: doc.name,
                pdfUrl: pdfUrl,
              };
            }
          }
          return doc;
        });
      props.appState.handlePatientTaskInstanceReceived(
        newPatientTaskInstanceState.patientTaskInstance
      );
    },
    [patientTaskInstanceState, props.appState, props.patientDocumentTypeId]
  );

  const handleAnswerQuestion = React.useCallback(
    (
      id: number,
      row: number,
      value: any,
      onSuccess?: () => void,
      createdAt?: Instant,
      vitalExtraDetails?: VitalExtraDetails
    ) => {
      return props.api
        .answerPatientTaskInstanceDocumentQuestion(
          props.patientTaskInstanceId,
          props.patientDocumentTypeId,
          {
            answer: value,
            chartRow: row,
            questionId: id,
            createdAt: createdAt,
            vitalExtraDetails: vitalExtraDetails !== undefined ? vitalExtraDetails : undefined,
          }
        )
        .then(() => {
          if (onSuccess !== undefined) {
            onSuccess();
          }
        });
    },
    [props]
  );

  const handleSubmitForm = React.useCallback(
    (submissionDate: LocalDate | null, regenerateNursingQuestionsOtherDocuments: boolean) => {
      if (patientTaskInstanceState.type !== "Ready") {
        throw new Error("Task not ready");
      }
      const date = submissionDate === null ? LocalDate.now() : submissionDate;
      const patientDocumentSubmissionDate: PatientDocumentTaskInstanceSubmitRequest = {
        date,
        regenerateNursingQuestionsOtherDocuments,
      };

      return props.api
        .postSubmitTaskInstancePatientDocument(
          props.patientTaskInstanceId,
          props.patientDocumentTypeId,
          patientDocumentSubmissionDate
        )
        .then(() => {
          handleCompleteDocument(null);
          props.onClickGoBack.call(null);
        });
    },
    [
      handleCompleteDocument,
      patientTaskInstanceState.type,
      props.api,
      props.onClickGoBack,
      props.patientDocumentTypeId,
      props.patientTaskInstanceId,
    ]
  );

  const handleScanUpload = React.useCallback(
    (base64: string, showLoader: () => void, hideLoader: () => void) => {
      if (documentState.type === "Ready" && patientTaskInstanceState.type === "Ready") {
        const data: PatientDocumentScanCreate = {
          base64: base64,
          documentId: documentState.document.documentType,
          documentVersion: documentState.document.versionId,
          documentTitle: null,
        };

        showLoader();

        props.api
          .uploadScannedDocumentByTaskInstanceId(props.patientTaskInstanceId, data)
          .then((res) => {
            handleCompleteDocument(res.url);
            props.onClickGoBack.call(null);
          })
          .catch(() => {
            simpleAlert({
              icon: "error",
              message: translate(STRINGS.UPLOAD_ERROR),
              title: translate(STRINGS.ERROR),
            });
            hideLoader();
          });
      }
    },
    [
      documentState,
      handleCompleteDocument,
      patientTaskInstanceState.type,
      props.api,
      props.onClickGoBack,
      props.patientTaskInstanceId,
    ]
  );

  const handleGetPatientMedicationProfile = React.useCallback(() => {
    return props.api.getPatientMedicationProfile(props.patientId);
  }, [props.api, props.patientId]);

  const handleCreateMedication = React.useCallback(
    (params: PatientMedicationProfileCreateParams) => {
      return props.api.postPatientMedicationByTaskInstanceId(props.patientTaskInstanceId, params);
    },
    [props.patientTaskInstanceId, props.api]
  );

  const handleUpdatedMedication = React.useCallback(
    (medication: PatientMedicationProfileWithDetails) => {
      return props.api.updatePatientMedicationByTaskInstanceId(props.patientTaskInstanceId, {
        id: medication.id,
        dose: medication.dose,
        frequency: medication.frequency,
        from: medication.from,
        medicationId: medication.medicationId,
        status: "ACTIVE",
        until: medication.until,
        notes: medication.notes,
      });
    },
    [props.api, props.patientTaskInstanceId]
  );

  const handleDeleteMedication = React.useCallback(
    (medication: PatientMedicationProfileWithDetails) => {
      return props.api.updatePatientMedicationByTaskInstanceId(props.patientTaskInstanceId, {
        id: medication.id,
        dose: medication.dose,
        frequency: medication.frequency,
        from: medication.from,
        medicationId: medication.medicationId,
        status: "DISCONTINUED",
        until: medication.until,
        notes: medication.notes,
      });
    },
    [props.api, props.patientTaskInstanceId]
  );

  const handleCreateDiagnosis = React.useCallback(
    (params: PatientContractDiagnosisSetParams) => {
      return props.api.createPatientDiagnosisCodeByTaskInstanceId(props.patientTaskInstanceId, {
        icdCode: params.icdCode,
        startDate: params.startDate,
        endDate: params.endDate,
        type: params.type,
      });
    },
    [props.api, props.patientTaskInstanceId]
  );

  const handleEditDiagnosis = React.useCallback(
    (id: PatientContractDiagnosisCodeId, params: PatientContractDiagnosisSetParams) => {
      return props.api.updatePatientDiagnosisCode(id, params);
    },
    [props.api]
  );

  const handleDeleteDiagnosis = React.useCallback(
    (id: PatientContractDiagnosisCodeId) => {
      return props.api.deletePatientDiagnosisCode(id);
    },
    [props.api]
  );

  const handleGetLastAnswers = React.useCallback((): Promise<PatientDocumentAnswers> => {
    if (documentState.type !== "Ready") {
      return new Promise((resolve, _reject) => {
        resolve({ answers: [] });
      });
    }

    return props.api.getDocumentLastAnswersByTaskInstanceId(
      props.patientTaskInstanceId,
      documentState.document.documentType,
      documentState.document.versionId
    );
  }, [documentState, props.api, props.patientTaskInstanceId]);

  const handleGetPatientContractDiagnosisCodes = React.useCallback(() => {
    return props.api.getPatientDiagnosisCodesByTaskInstanceId(props.patientTaskInstanceId);
  }, [props.api, props.patientTaskInstanceId]);

  const handleGetScheduledDate = React.useCallback(() => {
    if (patientTaskInstanceState.type !== "Ready") {
      return null;
    }

    const zoneId = ZoneId.of("America/New_York");

    return (
      fmap(patientTaskInstanceState.patientTaskInstance.scheduleDateTime, (x) =>
        LocalDateTime.ofInstant(x, zoneId).toLocalDate()
      ) ?? patientTaskInstanceState.patientTaskInstance.startDate
    );
  }, [patientTaskInstanceState]);

  const handleValidateSubmissionDate = React.useCallback(() => {
    const scheduledDate = handleGetScheduledDate();

    if (scheduledDate === null) {
      return false;
    }

    const zoneId = ZoneId.of("America/New_York");

    return LocalDate.now(zoneId).equals(scheduledDate);
  }, [handleGetScheduledDate]);

  const handleGetIcdCodeAutoCompleteOptions = React.useCallback(
    (searchText: string) => {
      return props.api.getIcdCodeAutoCompleteOptions(searchText);
    },
    [props.api]
  );

  const handleSearchMedications = React.useCallback(
    (value: string) => {
      if (patientTaskInstanceState.type !== "Ready") {
        throw new Error(
          `Can't search medications while task instance state is ${patientTaskInstanceState.type}`
        );
      }

      return props.api.searchMedicationsByPatientId(
        value,
        patientTaskInstanceState.patientTaskInstance.taskDetails.patientId
      );
    },
    [patientTaskInstanceState, props.api]
  );

  const documentReplaceConstants = React.useMemo((): DocumentReplaceConstants => {
    const constants: DocumentReplaceConstants = {
      agencyName: "agencyName",
      caregiverName: "caregiverName",
      patientName: "patientName",
      agencyAddress: "agencyAddress",
      agencyPhone: "agencyPhone",
    };

    if (patientTaskInstanceState.type === "Ready") {
      constants.patientName = patientTaskInstanceState.patientTaskInstance.taskDetails.patientName;
      constants.agencyName = patientTaskInstanceState.patientTaskInstance.taskDetails.agencyName;
      constants.agencyAddress =
        patientTaskInstanceState.patientTaskInstance.taskDetails.agencyAddress;
      constants.agencyPhone = patientTaskInstanceState.patientTaskInstance.taskDetails.agencyPhone;
    }
    if (!isLoading(props.appState.appCaregiverState)) {
      constants.caregiverName =
        props.appState.appCaregiverState.caregiver.firstName +
        " " +
        props.appState.appCaregiverState.caregiver.lastName;
    }

    return constants;
  }, [patientTaskInstanceState, props.appState.appCaregiverState]);

  const resubmissionReason = React.useMemo(() => {
    if (patientTaskInstanceState.type !== "Ready") {
      return null;
    }

    return getResubmissionReasonFromDocuments(
      patientTaskInstanceState.patientTaskInstance.taskDetails.documents,
      props.patientDocumentTypeId
    );
  }, [patientTaskInstanceState, props.patientDocumentTypeId]);

  switch (documentState.type) {
    case "Loading":
      return <LoadingScaffold onClickGoBack={props.onClickGoBack} />;
    case "NotFound":
      return <NotFoundScaffold onClickGoBack={props.onClickGoBack} />;
    case "Ready":
      return (
        <ScheduledVisitDocScreen<ScheduledVisitDocScreenProps>
          onCheckForOtherNursingQuestionsOnDocuments={() =>
            props.api.checkForOtherNursingQuestionsOnDocuments({
              documentTypeId: props.patientDocumentTypeId,
              taskInstanceId: props.patientTaskInstanceId,
            })
          }
          addNewPhysician={(...params) => props.api.addNewPhysician(...params)}
          onClickAddPhysician={props.onClickAddPhysician}
          getPatientPrimaryPhysician={(...params) =>
            props.api.getPatientPrimaryPhysician(...params)
          }
          getAllPhysicians={(...params) => props.api.getAllPhysicians(...params)}
          setPatientPhysician={(...params) => props.api.setPatientPhysician(...params)}
          patientId={props.patientId}
          key={
            documentState.document.documentType.toString() +
            "-" +
            documentState.document.versionId.toString()
          }
          title={documentState.document.title}
          docFormat={documentState.document.content}
          initialAnswers={documentState.document.answers}
          readonly={documentState.document.readonly}
          skipChartTree={false}
          documentReplaceConstants={documentReplaceConstants}
          isScannable={documentState.document.isScannable}
          getIcdCodeAutoCompleteOptions={handleGetIcdCodeAutoCompleteOptions}
          getLastAnswers={handleGetLastAnswers}
          getScheduledDate={handleGetScheduledDate}
          searchMedications={handleSearchMedications}
          getPatientContractDiagnosisCodes={handleGetPatientContractDiagnosisCodes}
          onBackClick={props.onClickGoBack}
          onValidateSubmissionDate={handleValidateSubmissionDate}
          onAnswerQuestion={handleAnswerQuestion}
          onSubmitForm={handleSubmitForm}
          onScanUpload={handleScanUpload}
          getPatientMedicationProfile={handleGetPatientMedicationProfile}
          onCreateMedication={handleCreateMedication}
          onUpdatedMedication={handleUpdatedMedication}
          onDeleteMedication={handleDeleteMedication}
          onCreateDiagnosis={handleCreateDiagnosis}
          onEditDiagnosis={handleEditDiagnosis}
          onDeleteDiagnosis={handleDeleteDiagnosis}
          resubmissionReason={resubmissionReason}
        />
      );
    default:
      assertNever(documentState);
  }
};

export default React.memo(PatientTaskInstanceDocumentActivity);
